import {Spinner} from '@dropbox/dig-components/progress_indicators';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Stack} from '@dropbox/dig-foundations';
import {useQuery} from '@tanstack/react-query';
import {pulseUserAtom} from 'atoms/auth';
import type {EmployeeRoleLevel} from 'client';
import {OpenAPI} from 'client/core/OpenAPI';
import {request as __request} from 'client/core/request';
import {Avatar} from 'components/DSYS/Avatar';
import {PersonHoverCard} from 'components/DSYS/HoverCard';
import {Layout} from 'components/DSYS/Layout';
import {Link} from 'components/DSYS/Link';
import {StatusChip} from 'components/DSYS/StatusChip';
import {Column, Table} from 'components/DSYS/Table';
import {Title} from 'components/DSYS/Title';
import {ROUTE_PATHS} from 'constant';
import {isSuperUser} from 'helpers/utils';
import {useDocumentTitle} from 'hooks/useDocumentTitle';
import {useAtomValue} from 'jotai';
import {useMemo} from 'react';
import {useNavigate} from 'react-router';
import {getWorkstreamUpdateVisualStatus} from 'views/workstreams/utils';

type AutoArchiveCandidate = {
  id: number;
  name: string;
  created_at: string;
  dri: EmployeeRoleLevel | null;
  latest_update_at: string | null;
  latest_update_status: string | null;
};

type AutoArchivePreview = {
  quarter_start: string;
  quarter_end: string;
  archive_run_at: string;
  reminder_send_date: string;
  candidates: AutoArchiveCandidate[];
};

const fetchAutoArchivePreview = async (): Promise<AutoArchivePreview> =>
  __request(OpenAPI, {
    method: 'GET',
    url: '/api/v1/workstream/admin/auto-archive',
  }) as Promise<AutoArchivePreview>;

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

const dateTimeFormatter = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

const parseApiDate = (value: string) => {
  const datePart = value.split('T')[0];
  if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
    const [year, month, day] = datePart.split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  return new Date(value);
};

const parseApiDateTime = (value: string) => {
  if (/^\d{4}-\d{2}-\d{2}T/.test(value) && !/(Z|[+-]\d{2}:?\d{2})$/.test(value)) {
    return new Date(`${value}Z`);
  }
  return parseApiDate(value);
};

const formatDate = (value?: string | null) => {
  if (!value) return 'None';
  const date = parseApiDate(value);
  if (Number.isNaN(date.getTime())) return 'None';
  return dateFormatter.format(date);
};

const formatDateTime = (value?: string | null) => {
  if (!value) return 'None';
  const date = parseApiDateTime(value);
  if (Number.isNaN(date.getTime())) return 'None';
  return dateTimeFormatter.format(date);
};

const compareString = (left: string, right: string) => left.localeCompare(right);

const getStatusValue = (status?: string | null) => status || 'no_status';

const getSortableTime = (value?: string | null) => {
  if (!value) return 0;
  const date = parseApiDateTime(value);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
};

const columns: Column<AutoArchiveCandidate>[] = [
  {
    type: 'Workstream',
    fixed: true,
    minWidth: 320,
    sort: (a, b) => compareString(a.name, b.name),
  },
  {
    type: 'DRI',
    fixed: true,
    minWidth: 240,
    sort: (a, b) => compareString(a.dri?.name ?? '', b.dri?.name ?? ''),
  },
  {
    type: 'Created date',
    minWidth: 160,
    sort: (a, b) => getSortableTime(a.created_at) - getSortableTime(b.created_at),
  },
  {
    type: 'Latest status',
    minWidth: 160,
    sort: (a, b) =>
      compareString(getStatusValue(a.latest_update_status), getStatusValue(b.latest_update_status)),
  },
  {
    type: 'Latest update',
    minWidth: 180,
    sort: (a, b) => getSortableTime(a.latest_update_at) - getSortableTime(b.latest_update_at),
  },
];

const CandidateRow = ({candidate}: {candidate: AutoArchiveCandidate}) => (
  <Table.Row isSelectable>
    <Table.Cell>
      <Link
        to={ROUTE_PATHS.WORKSTREAMS_DETAIL.replace(':id', String(candidate.id))}
        monochromatic
        hasNoUnderline
        showUnderlineOnHover
      >
        <Text>{candidate.name}</Text>
      </Link>
    </Table.Cell>
    <Table.Cell>
      {candidate.dri ? (
        <PersonHoverCard
          ldap={candidate.dri.ldap}
          name={candidate.dri.name}
          role={candidate.dri.role}
        >
          <Link
            to={ROUTE_PATHS.PROFILE.replace(':userid', candidate.dri.ldap)}
            monochromatic
            hasNoUnderline
            showUnderlineOnHover
            style={{display: 'inline-flex', alignItems: 'center', gap: 6}}
          >
            <Avatar user={candidate.dri} size="xsmall" />
            <Text>{candidate.dri.name}</Text>
          </Link>
        </PersonHoverCard>
      ) : (
        <Text color="subtle">None</Text>
      )}
    </Table.Cell>
    <Table.Cell>
      <Text>{formatDate(candidate.created_at)}</Text>
    </Table.Cell>
    <Table.Cell>
      <StatusChip
        status={getStatusValue(candidate.latest_update_status)}
        asStatus={getWorkstreamUpdateVisualStatus(candidate.latest_update_status)}
        includeArchived
      />
    </Table.Cell>
    <Table.Cell>
      <Text>{formatDateTime(candidate.latest_update_at)}</Text>
    </Table.Cell>
  </Table.Row>
);

export const AdminWorkstreamAutoArchive = () => {
  const navigate = useNavigate();
  const pulseUser = useAtomValue(pulseUserAtom);
  useDocumentTitle('Workstream auto-archive');

  const {data, isError, isLoading} = useQuery({
    queryKey: ['admin', 'workstream-auto-archive'],
    queryFn: fetchAutoArchivePreview,
  });

  const candidateCount = data?.candidates.length ?? 0;
  const quarterLabel = useMemo(() => {
    if (!data) return '';
    return `${formatDate(data.quarter_start)} - ${formatDate(data.quarter_end)}`;
  }, [data]);

  if (!pulseUser) {
    return null;
  }

  if (!isSuperUser(pulseUser)) {
    navigate('/');
    return null;
  }

  return (
    <Layout.Container
      breadcrumb={[
        {children: 'Admin', to: ROUTE_PATHS.ADMIN},
        {children: 'Workstream auto-archive', to: ROUTE_PATHS.ADMIN_WORKSTREAM_AUTO_ARCHIVE},
      ]}
    >
      <Stack gap="24">
        <Stack gap="8">
          <Title size={24}>Workstream auto-archive</Title>
          {data ? (
            <Text color="subtle">
              {candidateCount} matching workstream{candidateCount === 1 ? '' : 's'} for{' '}
              {quarterLabel}
            </Text>
          ) : null}
        </Stack>

        {data ? (
          <Box
            className={atoms({rowGap: '16', columnGap: '44'})}
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            }}
          >
            <Box display="block">
              <Box display="block" style={{marginBottom: 6}}>
                <Text color="subtle" size="small">
                  Archive run
                </Text>
              </Box>
              <Box display="block">
                <Text>{formatDateTime(data.archive_run_at)}</Text>
              </Box>
            </Box>
            <Box display="block">
              <Box display="block" style={{marginBottom: 6}}>
                <Text color="subtle" size="small">
                  Reminder date
                </Text>
              </Box>
              <Box display="block">
                <Text>{formatDate(data.reminder_send_date)}</Text>
              </Box>
            </Box>
          </Box>
        ) : null}

        <Table
          columns={columns}
          data={data?.candidates ?? []}
          sortConfig={{columnId: 'Workstream', asc: true}}
          compact
        >
          <Table.Header />
          <Table.Body>
            {isLoading ? (
              <Table.Row>
                <Table.Cell colSpan={columns.length}>
                  <Box display="flex" alignItems="center" style={{gap: 8}}>
                    <Spinner size="small" />
                    <Text color="subtle">Loading workstreams</Text>
                  </Box>
                </Table.Cell>
              </Table.Row>
            ) : isError ? (
              <Table.Row>
                <Table.Cell colSpan={columns.length}>
                  <Text color="subtle">Unable to load workstreams.</Text>
                </Table.Cell>
              </Table.Row>
            ) : candidateCount === 0 ? (
              <Table.Row>
                <Table.Cell colSpan={columns.length}>
                  <Text color="subtle">No workstreams currently match.</Text>
                </Table.Cell>
              </Table.Row>
            ) : (
              <Table.Rows renderRow={(candidate) => <CandidateRow candidate={candidate} />} />
            )}
          </Table.Body>
        </Table>
      </Stack>
    </Layout.Container>
  );
};
